import { ReactNode, useState, useMemo } from 'react'
import { Container, BottomNav, NavTab, Modal, Button, Loading } from '@talktoast/ui'
import { useSessionCheck, sessionApiService, useUserId, useAuth } from '@talktoast/shared'
import { ICONS, getIconSize } from '@talktoast/shared'
import ChatBottomSheet from './ChatBottomSheet'
import Header from './Header'

interface LayoutProps {
  children: ReactNode
  hideBottomNav?: boolean
  /**
   * Optional callback for header navigation (used for view state management in Home)
   */
  onHeaderNavigate?: (path: string) => void
  /**
   * Optional callback for bottom nav navigation (used for view state management in Home)
   */
  onBottomNavNavigate?: (view: 'home' | 'calendar' | 'history') => void
  /**
   * Optional callback for Plan button click (starts new meal planning session)
   */
  onPlanClick?: () => void
  /**
   * Optional callback for resuming an existing session
   */
  onResumeSession?: (sessionId: string, status: string) => void
  /**
   * Current active view for bottom nav highlighting
   */
  activeView?: string
}

export default function Layout({ children, hideBottomNav = false, onHeaderNavigate, onBottomNavNavigate, onPlanClick, onResumeSession, activeView = 'home' }: LayoutProps) {
  const userId = useUserId()
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [showSessionModal, setShowSessionModal] = useState(false)

  // Use consolidated session checking hook
  const { hasSession, session: activeSession, unassignedCount, checkForActiveSession, isChecking } = useSessionCheck()

  // Determine current screen for chat context based on activeView
  const currentScreen = useMemo(() => {
    if (activeView === 'calendar') return 'calendar'
    if (activeView === 'history') return 'history'
    return 'home'
  }, [activeView])

  const handlePlanClick = async () => {
    // Check for active session (planning, shopping, scheduling statuses)
    if (!userId) {
      onPlanClick?.()
      return
    }

    const result = await checkForActiveSession()

    if (result.hasSession) {
      // Found active session - show resume modal
      setShowSessionModal(true)
      return
    }

    // No active session - start new meal planning session
    onPlanClick?.()
  }

  const handleChatClick = () => {
    setIsChatOpen(true)
  }

  const handleResumeSession = () => {
    setShowSessionModal(false)
    if (!activeSession) return

    // Call the new resume callback if available, otherwise fall back to onPlanClick
    if (onResumeSession) {
      // Direct session resume - no modal, straight to the right phase
      onResumeSession(activeSession.id, activeSession.status)
    } else {
      // Fallback to old behavior if callback not provided
      switch (activeSession.status) {
        case 'planning':
          onPlanClick?.()
          break
        case 'shopping':
          onPlanClick?.()
          break
        case 'scheduling':
          onBottomNavNavigate?.('calendar')
          break
        default:
          onPlanClick?.()
      }
    }
  }

  const handleStartFresh = async () => {
    setShowSessionModal(false)

    // Mark old session as complete
    if (activeSession && userId) {
      try {
        const { backendApiService } = await import('@talktoast/shared')
        await backendApiService.updateMealSessionStatus(activeSession.id, 'complete')
        console.log('[Layout] Marked old session as complete:', activeSession.id)
      } catch (error) {
        console.error('[Layout] Error marking session as complete:', error)
      }
    }

    // Clear active session ID and start new meal planning session
    sessionApiService.clearActiveSessionId()
    onPlanClick?.()
  }

  const handleSendChatMessage = async (message: string) => {
    // Chat feature not yet implemented - showing "Still Toasting" placeholder
    console.log('[Layout] Chat not yet available')
  }

  return (
    <div className="min-h-screen min-h-dvh bg-gray-50">
      <div className="mx-auto max-w-md bg-white shadow-lg min-h-full">
        {/* Header - Sticky at top */}
        <div className="sticky top-0 z-30">
          <Header onNavigate={onHeaderNavigate} />
        </div>

        {/* Main content with padding for bottom nav */}
        <main className={`p-4 ${!hideBottomNav ? 'pb-20' : 'pb-4'}`}>
          {children}
        </main>

        {/* Bottom Navigation - 4 tabs: Today | Calendar | Plan | History */}
        {!hideBottomNav && (
          <BottomNav>
            <NavTab
              icon={<ICONS.navigation.home className={getIconSize('md')} />}
              label="Today"
              active={activeView === 'home'}
              onClick={() => onBottomNavNavigate?.('home')}
            />
            <NavTab
              icon={<ICONS.navigation.calendar className={getIconSize('md')} />}
              label="Calendar"
              active={activeView === 'calendar'}
              onClick={() => onBottomNavNavigate?.('calendar')}
            />
            <NavTab
              icon={isChecking ? <Loading variant="spinner" size="sm" className={getIconSize('md')} /> : <ICONS.navigation.add className={getIconSize('md')} />}
              label="Plan"
              active={activeView === 'planning' || activeView === 'session'}
              onClick={handlePlanClick}
              disabled={isChecking}
            />
            <NavTab
              icon={<ICONS.utility.clock className={getIconSize('md')} />}
              label="History"
              active={activeView === 'recipe-history' || activeView === 'favorites'}
              onClick={() => onBottomNavNavigate?.('history')}
            />
          </BottomNav>
        )}

        {/* Session Resume Modal */}
        {showSessionModal && activeSession && (
          <Modal
            open={showSessionModal}
            onClose={() => setShowSessionModal(false)}
            title={
              activeSession.status === 'scheduling' && unassignedCount > 0
                ? 'You have an incomplete meal plan'
                : 'Continue Your Meal Plan?'
            }
          >
            <p className="text-gray-600 mb-4">
              {activeSession.status === 'scheduling' && unassignedCount > 0 ? (
                <>
                  You have <strong>{unassignedCount} unscheduled {unassignedCount === 1 ? 'meal' : 'meals'}</strong> from your previous planning session.
                </>
              ) : (
                'You have an in-progress meal planning session. Would you like to continue where you left off or start fresh?'
              )}
            </p>

            {activeSession.status === 'scheduling' && unassignedCount > 0 && (
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-900 flex items-center gap-2">
                  <ICONS.utility.idea className={getIconSize('sm')} />
                  <strong>What would you like to do?</strong>
                </p>
                <ul className="mt-2 text-sm text-blue-800 space-y-1">
                  <li>• Schedule your pending meals on the calendar</li>
                  <li>• Start a new meal plan (old meals stay available for 7 days)</li>
                </ul>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <Button
                variant="primary"
                fullWidth
                onClick={handleResumeSession}
              >
                {activeSession.status === 'scheduling' ? (
                  <span className="flex items-center justify-center gap-2">
                    <ICONS.navigation.calendar className={getIconSize('sm')} />
                    Go to Calendar
                  </span>
                ) : (
                  'Continue Existing Session'
                )}
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={handleStartFresh}
              >
                {activeSession.status === 'scheduling' ? (
                  <span className="flex items-center justify-center gap-2">
                    <ICONS.utility.magic className={getIconSize('sm')} />
                    Start Fresh
                  </span>
                ) : (
                  'Start Fresh'
                )}
              </Button>
            </div>

            {!(activeSession.status === 'scheduling' && unassignedCount > 0) && (
              <p className="text-xs text-gray-400 mt-4 text-center">
                Status: {(activeSession.status === 'created' || activeSession.status === 'planning') && 'Planning'}
                {activeSession.status === 'shopping' && 'Shopping List'}
                {activeSession.status === 'scheduling' && 'Calendar'}
              </p>
            )}
          </Modal>
        )}

        {/* Chat Bottom Sheet */}
        <ChatBottomSheet
          isOpen={isChatOpen}
          onClose={() => setIsChatOpen(false)}
          currentScreen={currentScreen}
          onSendMessage={handleSendChatMessage}
        />
      </div>
    </div>
  )
}